import _ from 'lodash';
import store from '../state/store';
import normalizePhoneNumber from './normalizePhoneNumber';
// import debugContactMatching from './debugContactMatching';

import {
  selectContacts,
  selectContactsEntities,
  selectContactsIds,
  selectContactsNames,
  selectContactsPhones,
  selectContactsUsers,
} from '../state/contacts/contactsSlice';

/**
 * Enhanced Contact Matching Solution
 *
 * This utility addresses a critical issue where conversations displayed phone numbers instead of contact names,
 * particularly after message send failures (400/500 errors). The system was creating auto-generated
 * "custom contacts" instead of finding existing contacts from the contacts page.
 *
 * Key Problems Solved:
 * 1. Phone numbers displayed instead of contact names
 * 2. Various phone number formats not being matched (+1, country codes, no country code, etc.)
 * 3. Custom contacts taking precedence over real contacts
 * 4. Contact names reverting to phone numbers after message send errors
 *
 * Solution Architecture:
 * - Prioritizes real contacts over auto-generated custom contacts
 * - Uses normalizePhoneNumber() for comprehensive phone format matching
 * - Implements direct contact search as fallback when indexed lookup fails
 * - Maintains contact information persistence through all message operations
 *
 * Enhanced contact matching with better phone number normalization
 * Matches string by name, user, uid or phone number with multiple format attempts
 * @param str - The search string (phone number, name, user, or uid)
 * @returns {null|*} - Matched contact object or null if no match found
 */
export default function enhancedMatchContact(str) {
  if (!str) {
    return null;
  }

  const state = store.getState();
  const allContacts = selectContacts(state);
  const contactsIds = selectContactsIds(state);
  const contactsEntities = selectContactsEntities(state);
  const contactsNames = selectContactsNames(state);
  const contactsUsers = selectContactsUsers(state);
  const contactsPhones = selectContactsPhones(state);

  // Debug logging - enable with: localStorage.setItem('debugContactMatching', 'true')
  // if (localStorage.getItem('debugContactMatching') === 'true') {
  //   debugContactMatching(str);
  // }

  if (!contactsIds || !contactsIds.length) {
    return null;
  }

  const searchString = _.toString(str).toLowerCase();

  // search by name
  if (contactsNames[searchString]) {
    return contactsEntities[contactsNames[searchString]];
  }

  // match by user
  if (contactsUsers[searchString]) {
    const contactId = contactsUsers[searchString];
    const entity = contactsEntities[contactId];
    if (entity) {
      return entity;
    }
  }

  // match by uid
  const uid = searchString.split('@');
  if (uid && uid.length && contactsUsers[uid[0]]) {
    const contactId = contactsUsers[uid[0]];
    const entity = contactsEntities[contactId];
    if (entity) {
      return entity;
    }
  }

  // Enhanced phone number matching with multiple format attempts
  const phoneVariants = normalizePhoneNumber(str);

  // First try to find real contacts (non-custom contacts)
  let realContact = null;
  let customContact = null;

  phoneVariants.some((variant) => {
    if (contactsPhones[variant]) {
      const contact = contactsEntities[contactsPhones[variant]];
      if (contact) {
        if (contact.customContact === true) {
          // Store custom contact as fallback
          if (!customContact) {
            customContact = contact;
          }
        } else {
          // Found a real contact - prioritize this
          realContact = contact;
          return true; // Break out of some() loop
        }
      }
    }
    return false; // Continue searching
  });

  // If no contact found through indexed search, try direct search through all real contacts
  if (!realContact && allContacts && allContacts.length > 0) {
    const directPhoneVariants = normalizePhoneNumber(str);

    // Search through real contacts (non-custom) first
    const directMatch = allContacts.find((contact) => {
      if (contact.customContact === true) return false;

      const contactPhones = [
        contact.cell_phone,
        contact.work_phone,
        contact.home_phone,
        contact.user, // extension
      ].filter(Boolean);

      return contactPhones.some((phone) => {
        const phoneVars = normalizePhoneNumber(phone);
        return phoneVars.some((variant) => directPhoneVariants.includes(variant));
      });
    });

    if (directMatch) {
      return directMatch;
    }
  }

  // Return real contact if found, otherwise return custom contact, otherwise null
  return realContact || customContact || null;
}
